import { BankAccount } from './../models/billing.model'
import { Injectable } from '@angular/core'
import { map, Observable, of } from 'rxjs'

import { environment } from 'src/environments/environment'
import { FALLBACK_XERO_ACCOUNTS, FALLBACK_XERO_CREDENTIALS } from 'src/environments/go-api-fallbacks'

import { ApiService } from './api.service'

import { withFeedback } from '@app/core/interceptors/feedback-context'

import { BillingQuantities, BillingPricing, Invoice, BillingSummary, StripeCardKeys, PaymentMethods } from '@shared/models/billing.model'
import { XeroCredentials, XeroAccount, StripeKeys } from '../models/billing.model'
@Injectable({ providedIn: 'root' })
export class BillingService {
  billingPricing: BillingPricing
  billingQuantities: BillingQuantities

  constructor(private apiService: ApiService) {}

  getBillingQuantities(id: number | string): Observable<BillingQuantities> {
    return this.apiService
      .get(`/billing/${id}`)
      .pipe(map((response) => (this.billingQuantities = new BillingQuantities(<BillingQuantities>response['data']))))
  }

  getBillingSummary(): Observable<BillingSummary> {
    return this.apiService.get('/billing/summary')
  }

  getBillingPricing(id: number | string): Observable<BillingPricing> {
    return this.apiService
      .get(`/billing/plan/${id}`)
      .pipe(map((response) => (this.billingPricing = new BillingPricing(<BillingPricing>response['data']))))
  }

  patchBillingPricing(id: number, data: Partial<BillingPricing>): Observable<BillingPricing> {
    return this.apiService.patch(`/billing/plan/${id}`, data, { context: withFeedback() })
  }

  getStripeSetupIntent(): Observable<StripeCardKeys> {
    return this.apiService.get('/stripe/setup-intent').pipe(map((response) => <StripeCardKeys>response['data']))
  }

  sendSetupIntent(id: string): Observable<void> {
    return this.apiService.post('/stripe/mandate', { setup_intent: id }, { context: withFeedback() })
  }

  getPaymentMethods(): Observable<PaymentMethods> {
    return this.apiService.get('/payment/methods').pipe(map((response) => <PaymentMethods>response['data']))
  }

  selectDefaultPaymentMethod(id: string): Observable<void> {
    return this.apiService.post(`/payment/methods/${id}/default`, null, { context: withFeedback() })
  }

  deletePaymentMethod(id: string): Observable<void> {
    return this.apiService.delete(`/payment/methods/${id}`, { context: withFeedback() })
  }

  toggleManualPaymentMethod(toggle: 1 | 0): Observable<void> {
    // NOTE: no withFeedback — chained before selectDefaultPaymentMethod() in payment-methods.component
    // (toggle(0) -> set default); the success toast is owned by selectDefaultPaymentMethod to avoid a double.
    return this.apiService.post(`/payment/methods/manual?enable=${toggle}`, null)
  }

  getInvoice(id: number | string): Observable<Invoice> {
    return this.apiService.get(`/billing/invoice/${id}`)
  }

  downloadInvoice(id: number | string): Observable<string> {
    return this.apiService.get(`/billing/invoice/${id}/download`, { responseType: 'blob' })
  }

  payInvoice(id: number | string): Observable<Invoice> {
    return this.apiService.post(`/billing/invoice/${id}/pay`, null, { context: withFeedback() })
  }

  cleanInvoices(clientId: number | string): Observable<void> {
    return this.apiService.post(`/billing/client/${clientId}/invoice/clean`, null, { context: withFeedback() })
  }

  changePlan() {
    // NOTE: no withFeedback — callers (subscriptions/overview.component) already show their own
    // dialog.success('CHANGE_BILLING_PLAN_SUCCESS'); a toast here would duplicate it.
    return this.apiService.post(`/billing/change-plan-ntf`, {})
  }

  setXeroCredentials(data: XeroCredentials): Observable<void> {
    // TODO(xero-fake): remove fake branch once BE implements POST /xero/secret.
    if (environment.fakeApis) return of(undefined)
    return this.apiService.post('/xero/secret', data, { context: withFeedback() })
  }

  getXeroCredentials(): Observable<XeroCredentials> {
    // TODO(xero-fake): remove fake branch once BE implements GET /xero/secret.
    if (environment.fakeApis) return of(FALLBACK_XERO_CREDENTIALS as unknown as XeroCredentials)
    return this.apiService.get('/xero/secret')
  }

  getXeroAccounts(): Observable<XeroAccount[]> {
    // TODO(xero-fake): remove fake branch once BE implements GET /xero/accounts.
    if (environment.fakeApis) return of(FALLBACK_XERO_ACCOUNTS as unknown as XeroAccount[])
    return this.apiService.get('/xero/accounts')
  }

  getStripeKeys(): Observable<StripeKeys> {
    return this.apiService.get('/stripe/secret')
  }

  postStripeKeys(data: StripeKeys): Observable<void> {
    // NOTE: no withFeedback — chained before setAdminBankAccount() in stripe-form.component (saveData);
    // the success toast is owned by setAdminBankAccount to avoid a double.
    return this.apiService.post('/stripe/secret', data)
  }

  getAdminBankAccount(teamId: number): Observable<BankAccount> {
    return this.apiService.get(`/billing/${teamId}/settings`)
  }

  setAdminBankAccount(teamId: number, data: BankAccount): Observable<BankAccount> {
    return this.apiService.post(`/billing/${teamId}/settings`, data, { context: withFeedback() })
  }
}
